import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import IconAppointments from '../icons/IconAppointments';
import IconDashboard from '../icons/IconDashboard';
import IconMessages from '../icons/IconMessages';
import IconOffers from '../icons/IconOffers';
import IconSettings from '../icons/IconSettings';
import IconArrowRight from '../icons/IconArrowRight';
import IconMenu from '../icons/IconMenu';
import IconLogout from '../icons/IconLogout';
import { TitleProvider, useTitle } from '../hooks/useTitle';
import IconInventory from '../icons/IconInventory';
import useAuth from '../hooks/useAuth';
import DealerNotificationCenter from '../components/DealerNotificationCenter';
import { Toaster } from 'react-hot-toast';

const ACCENT = '#F43F60';
const SIDEBAR_WIDTH = '13.875rem';
const SIDEBAR_COLLAPSED_WIDTH = '4.5rem';
const MOBILE_SIDEBAR_WIDTH = '280px';

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background: #1B1C30;
  position: relative;
  width: 100%;
  overflow-x: hidden;
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${props => (props.collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH)};
  background: #222336;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 25px 15px 32px 17px;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  z-index: 1001;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.18);
  
  @media (max-width: 900px) {
    position: fixed;
    left: ${props => (props.mobileOpen ? '0' : `-${MOBILE_SIDEBAR_WIDTH}`)};
    width: ${MOBILE_SIDEBAR_WIDTH};
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 0;
    padding: 24px 15px 24px 17px;
  }
  
  @media (max-width: 480px) {
    width: 260px;
    left: ${props => (props.mobileOpen ? '0' : '-260px')};
    padding: 20px 15px 20px 17px;
  }
`;

const SidebarBackdrop = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: ${props => (props.mobileOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 1000;
    backdrop-filter: blur(4px);
    transition: opacity 0.3s ease;
  }
`;

const Logo = styled.div`
  padding: 10px 12px 0 10px;
  margin-bottom: 3.42rem;
  width: 100%;
  display: flex;
  justify-content: ${props => (props.collapsed ? 'center' : 'flex-start')};
  align-items: center;
  
  svg {
    width: 32px;
    height: 32px;
  }
  
  img {
    width: ${props => (props.collapsed ? '32px' : 'auto')};
    height: 32px;
    object-fit: contain;
  }
  
  @media (max-width: 900px) {
    margin-bottom: 2rem;
    justify-content: flex-start;
    
    img {
      width: auto;
    }
  }
  
  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
    padding: 8px 8px 0 8px;
  }
`;

const Nav = styled.nav`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  
  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.06rem 0.75rem;
  border-radius: 0.63rem;
  height: 3.375rem;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: ${props => (props.active === 'true' ? 600 : 500)};
  color: ${props => (props.active === 'true' ? '#fff' : '#B4B4B4')};
  background: ${props => (props.active === 'true' ? '#313151' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  div {
    font-size: 1rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(244, 63, 96, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  svg {
    color: ${props => (props.active === 'true' ? ACCENT : '#B4B4B4')};
    transition: color 0.2s ease;
  }
  
  &:hover {
    background: #313151;
    color: #fff;

    
    svg {
      color: ${ACCENT};
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 0.625rem;
    height: 3rem;
    border-radius: 0.5rem;
  }
`;

const NavItemLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1rem;
  white-space: nowrap;
  opacity: ${props => (props.collapsed ? 0 : 1)};
  transition: opacity 0.2s ease;
  
  @media (max-width: 900px) {
    opacity: 1;
  }
  
  @media (max-width: 480px) {
    font-size: 0.875rem;
    gap: 0.625rem;
  }
`;

const NavItemIcon = styled.div`
  svg {
    font-size: 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
  }
  
  @media (max-width: 480px) {
    svg {
      font-size: 1.25rem;
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const NavItemArrow = styled.div`
  opacity: ${props => (props.collapsed ? 0 : 1)};
  transition: opacity 0.2s ease;
  
  @media (max-width: 900px) {
    opacity: 1;
  }
  
  @media (max-width: 480px) {
  svg {
      width: 14px;
      height: 14px;
    }
  }
  
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: ${props => (props.collapsed ? 'center' : 'flex-start')};
  background: none;
  border: none;
  color: #f43f60;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 1.06rem 0.75rem;
  border-radius: 0.63rem;
  margin-top: auto;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(244, 63, 96, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: #313151;
    color: #fff;

  }
  
  svg {
    font-size: 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
    transition: color 0.2s ease;
  }
  
  @media (max-width: 900px) {
    justify-content: flex-start;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 0.625rem;
    gap: 0.75rem;
    
    svg {
      font-size: 1.25rem;
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const Main = styled.div`
  flex: 1;
  background: #1B1C30;
  min-height: 100vh;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0px 0px 32px 0px;
  margin-left: ${SIDEBAR_WIDTH};
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(100vw - ${SIDEBAR_WIDTH});
  max-width: calc(100vw - ${SIDEBAR_WIDTH});
  overflow-x: hidden;
  
  @media (max-width: 900px) {
    margin-left: 0;
    padding: 0px 0px 24px 0px;
    width: 100vw;
    max-width: 100vw;
  }
  
  @media (max-width: 480px) {
    padding: 0px 0px 16px 0px;
  }
`;

const TopBar = styled.div`
  position: sticky;
  background: #1B1C30;
  padding: 0 22px;
  padding-top: 32px;
  padding-bottom: 16px;
  z-index: 1000;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 900px) {
    padding: 24px 16px 16px 16px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 12px 12px 12px;
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const MenuButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  margin-right: 12px;
  display: none;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  @media (max-width: 900px) {
    display: block;
  }
  
  svg {
    width: 19px;
    height: 16.1735px;
    transition: color 0.2s ease;
  }
  
  &:hover {
    color: ${ACCENT};
    background: rgba(244, 63, 96, 0.1);
  }
  
  @media (max-width: 480px) {
    margin-right: 8px;
    padding: 6px;
    
    svg {
      width: 16px;
      height: 14px;
    }
  }
`;

const PageTitle = styled.h1`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1.875rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 900px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
    flex: 1;
    min-width: 0;
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  background: #e3efff;
  color: #3b82f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Clash Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }
`;

const Content = styled.div`
  flex: 1;
  border-radius: 12px;
  padding: 35px 22px 0 22px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 900px) {
    padding: 24px 16px 0 16px;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    padding: 16px 12px 0 12px;
  }
`;

const navItems = [
  { label: 'Dashboard', icon: <IconDashboard />, path: '/dashboard' },
  { label: 'Inventory', icon: <IconInventory />, path: '/inventory' },
  { label: 'Offers', icon: <IconOffers />, path: '/offers' },
  { label: 'Appointments', icon: <IconAppointments />, path: '/appointments' },
  { label: 'Chats', icon: <IconMessages />, path: '/chats' },
  { label: 'Settings', icon: <IconSettings />, path: '/settings' },
];

const AuthLayoutInner = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { title } = useTitle();
  const { isDealerAuthenticated } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Redirect to home if not a dealer
  useEffect(() => {
    if (!isDealerAuthenticated) {
      window.location.href = '/';
    }
  }, [isDealerAuthenticated]);

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
      if (window.innerWidth > 900) {
        setMobileSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  };

  const handleBackdropClick = () => {
    setMobileSidebarOpen(false);
  };

  const handleNavItemClick = () => {
    if (isMobile) {
      setMobileSidebarOpen(false);
    }
  };

  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  const { user } = useAuth();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  
  return (
    <Wrapper>
      <SidebarBackdrop mobileOpen={mobileSidebarOpen} onClick={handleBackdropClick} />
      <Sidebar 
        collapsed={sidebarCollapsed && !isMobile} 
        mobileOpen={mobileSidebarOpen}
      >
        <Logo collapsed={sidebarCollapsed && !isMobile}>
          <img className="custom-img-fluid" src={'/logo.svg'} alt="logo" loading="lazy" />
        </Logo>
        <Nav>
          {navItems.map((item, idx) => (
            <NavItem
              key={item.label}
              to={item.path}
              active={(location.pathname === item.path).toString()}
              onClick={handleNavItemClick}
            >
              <NavItemLabel collapsed={sidebarCollapsed && !isMobile}>
                <NavItemIcon>{item.icon}</NavItemIcon>
                <div>{item.label}</div>
              </NavItemLabel>
              <NavItemArrow collapsed={sidebarCollapsed && !isMobile}>
                <IconArrowRight />
              </NavItemArrow>
            </NavItem>
          ))}
        </Nav>
        <LogoutButton
          type="button"
          onClick={handleLogout}
          collapsed={sidebarCollapsed && !isMobile}
          aria-label="Logout"
        >
          <IconLogout />
          <span style={{ display: sidebarCollapsed && !isMobile ? 'none' : 'inline' }}>
            Logout
          </span>
        </LogoutButton>
      </Sidebar>
      <Main
        style={{
          marginLeft: isMobile 
            ? 0 
            : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH),
          width: isMobile 
            ? '100vw' 
            : `calc(100vw - ${sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH})`,
          maxWidth: isMobile 
            ? '100vw' 
            : `calc(100vw - ${sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH})`,
        }}
      >
        <TopBar>
          <TopBarLeft>
            <MenuButton onClick={handleMenuClick} aria-label="Toggle sidebar">
              <IconMenu />
            </MenuButton>
            <PageTitle>{title}</PageTitle>
          </TopBarLeft>
          <TopBarRight>
            {/* Dealer Notification Center */}
            <DealerNotificationCenter token={token} />
          </TopBarRight>
        </TopBar>
        <Content>{children}</Content>
      </Main>
      
      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#303134',
            color: '#ededed',
            fontFamily: 'Clash Grotesk, sans-serif',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          },
          success: {
            style: {
              background: '#10B981',
              color: '#ffffff',
            },
          },
          error: {
            style: {
              background: '#EF4444',
              color: '#ffffff',
            },
          },
        }}
      />
    </Wrapper>
  );
};

const AuthLayout = ({ children, avatar }) => (
  <TitleProvider>
    <AuthLayoutInner avatar={avatar}>{children}</AuthLayoutInner>
  </TitleProvider>
);

export default AuthLayout;
